import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    LabelList,
    Cell,
    ResponsiveContainer,
    Text
} from 'recharts';
import classnames from 'classnames';
import { isEmpty, map, size } from 'lodash';

// local dependencies
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { widgetError as WidgetError } from 'ui/views/widgets/widgetError';
import { WIDGET_ERROR_TYPE } from 'constants/common';

/**
 * @type { object }
 * @desc A React presentation/dumb view component.
 * @example
 * import { barChart as BarChartView } from './barChart.view';
 *
 * // render
 * <BarChartView propName='propValue'/>
 */
export const barChartSuccess = ( props ) => {
	const {
		barCategoryGap,
		barSize,
		margin,
		minTickGap,
		XaxisLine,
		YaxisLine,
		YtickLine,
		XtickLine,
		data,
		interval,
		className,
	} = props;

	// fetching out styling class
	const widgetClassName = classnames( 'ui-bar-chart', className );

	const CustomizedTick = ( custom ) => {
        const { x, y, payload } = custom;
		return <Text x={x} y={y} width={75} textAnchor="middle" verticalAnchor="start">{payload.value}</Text>;
	};

	const maxVal = 0 < size( data ) ? Math.max.apply( Math, data.map( function( o ) { return o.value; } ) ) : 0;

	// const maxValue = Math.ceil( maxVal/5 )*5;

	return (
		<div className={ widgetClassName } style={ { width: props.width } }>
			{
				isEmpty( data ) ? (
					<div className='ui-table__body__no-data'>
						<WidgetError type={ WIDGET_ERROR_TYPE.NO_DATA_FOUND } label={ props.noDataFoundLabel }/>
					</div>
				) : (
					<ResponsiveContainer width="95%" height={ 300 } >
						<BarChart
							data={ data }
							barCategoryGap={ barCategoryGap }
							barSize={ barSize }
							margin={ margin }
						>
							{ /*  shows grid with Chart */ }
							<CartesianGrid vertical={ false } />

							{ /* To work with X-axis of chart several options are there */ }
							<XAxis
								dataKey="name"
								axisLine={ XaxisLine }
								tickLine={ XtickLine }
								interval={ interval }
								minTickGap={ minTickGap }
								dy={ 10 }
								onClick={ props.onClickOnXAxis }
								tick={ <CustomizedTick /> }
							/>

							<YAxis domain={ [ 0, maxVal ] } axisLine={ YaxisLine } tickLine={ YtickLine } />

							<Bar
								dataKey="value"
								onClick={ ( action, index ) => props.onClickAction( action, index ) }
								onMouseOver={ ( action, index ) => props.onHoverAction( action, index ) }
							>
								{ /* Label use to show at top of the each Bar, data key refer to key in JSON */ }
								<LabelList
									dataKey="labelText"
									position="top"
									offset={ 10 }
									fill="#000"
								/>
								{ map( data, ( entry, index ) => {
									const fill = index === props.activeIndex ? '#003e7b' : '#89bbe8';
									
									// mapping each bar using Cell Component based on different colors over click event
									return <Cell key={ `cell-${ index }` } fill={ fill } />;
								} ) }
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				)
			}
			
		</div>
	);
 };

// set display name
barChartSuccess.displayName = 'barChartSuccess';

// set default props
barChartSuccess.defaultProps = {};

// prop types
barChartSuccess.propTypes = {};

/* ******************************************************** */

export const barChartPreloader = () => {
    return (
        <div className='ui-bar-chart-preloader'>

            {/* chart */}
            <div className='ui-bar-chart-preloader__chart'>
                <div className='ui-bar-chart-preloader__chart__axes'>
                    <div className='ui-bar-chart-preloader__chart__axes__line' />
                    <div className='ui-bar-chart-preloader__chart__axes__line' />
                    <div className='ui-bar-chart-preloader__chart__axes__line' />
                    <div className='ui-bar-chart-preloader__chart__axes__line' />
                </div>
                <div className='ui-bar-chart-preloader__chart__bars-container'>
                    {
                        [ 1, 2, 3, 4, 5, 6, 7, 8, 9 ].map( ( index ) => {
                            return (
                                <div key={ index } className='ui-bar-chart-preloader__chart__bars-container__bar-group'>
                                    <div className='ui-bar-chart-preloader__chart__bars-container__bar-group__bars'>
                                        <div className='ui-bar-chart-preloader__chart__bars-container__bar-group__bars__bar' />
                                    </div>
                                    <div className='ui-bar-chart-preloader__chart__bars-container__bar-group__label'>
                                        <div className='ui-bar-chart-preloader__chart__bars-container__bar-group__label__text' />
                                    </div>
                                </div>
                            );
                        } )
                    }
                </div>
            </div>
        </div>
    );
};

// set display name
barChartPreloader.displayName = 'barChartPreloader';

// set default props
barChartPreloader.defaultProps = {};

// prop types
barChartPreloader.propTypes = {};

/* ******************************************************** */

export const barChart = withNetworkState( barChartPreloader, barChartSuccess );
